/**
 * AccountPage — route wrapper at /account.
 *
 * Ported from Yan-Vada Hero Pilot/components/auth-screens.jsx AccountA.
 * Sidebar nav layout (260px sidebar + main content area) on light Bawa Earth.
 * Shows profile info, status strip, editable form, danger zone.
 *
 * Requires auth — only accessible when signed in.
 */

import { useEffect, useState, type FormEvent, type MouseEvent as ReactMouseEvent } from 'react'
import { Link, Navigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { beginFullSignOutAndRestart, fullSignOutUrl } from '../lib/fullSignOut'

const NAV_ITEMS = ['Account', 'Proposals', 'Agreements', 'Invoices', 'Notifications'] as const

interface ProfileDraft {
  firstName: string
  lastName: string
  displayName: string
}

interface ProfileUpdateResponse {
  member?: {
    firstName?: string | null
    lastName?: string | null
    displayName?: string | null
    profileConfirmedAt?: string | null
  }
  error?: string
}

function profileDraftFromAuth(firstName: string | null, lastName: string | null, displayName: string | null, email: string | null): ProfileDraft {
  return {
    firstName: firstName ?? (email ? email.split('@')[0].split(/[._-]/)[0] : ''),
    lastName: lastName ?? '',
    displayName: displayName ?? '',
  }
}

function formatConfirmedAt(value: string | null) {
  if (!value) return null
  return new Intl.DateTimeFormat('en-GB', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(value))
}

export default function AccountPage() {
  const { email, isLoggedIn, status, displayName, firstName, lastName, profileImageUrl, profileType, rights, questionnaireUrl, profileConfirmedAt, loading, refreshAuth, signOut } = useAuth()
  const [localImageUrl, setLocalImageUrl] = useState<string | null>(profileImageUrl)
  const [profileDraft, setProfileDraft] = useState<ProfileDraft>(() => profileDraftFromAuth(firstName, lastName, displayName, email))
  const [uploading, setUploading] = useState(false)
  const [savingProfile, setSavingProfile] = useState(false)
  const [message, setMessage] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    setLocalImageUrl(profileImageUrl)
  }, [profileImageUrl])

  useEffect(() => {
    setProfileDraft(profileDraftFromAuth(firstName, lastName, displayName, email))
  }, [displayName, email, firstName, lastName])

  async function uploadProfileImage(file: File | null) {
    if (!file) return
    setUploading(true)
    setMessage(null)
    setError(null)
    try {
      const formData = new FormData()
      formData.set('file', file)
      const res = await fetch('/api/member/profile-image', {
        method: 'POST',
        credentials: 'include',
        body: formData,
      })
      const data = await res.json() as { url?: string; error?: string }
      if (!res.ok) throw new Error(data.error ?? 'Unable to upload profile image')
      setLocalImageUrl(data.url ?? null)
      setMessage('Profile image saved.')
      await refreshAuth()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to upload profile image')
    } finally {
      setUploading(false)
    }
  }

  if (loading) return <div className="account-page account-loading"><p>Checking account...</p></div>

  async function saveProfileDetails(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    setSavingProfile(true)
    setMessage(null)
    setError(null)
    try {
      const res = await fetch('/api/member/profile', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(profileDraft),
      })
      const data = await res.json() as ProfileUpdateResponse
      if (!res.ok) throw new Error(data.error ?? 'Unable to save profile details')
      if (data.member) {
        setProfileDraft(profileDraftFromAuth(data.member.firstName ?? null, data.member.lastName ?? null, data.member.displayName ?? null, email))
      }
      setMessage('Profile details saved and confirmed.')
      await refreshAuth()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to save profile details')
    } finally {
      setSavingProfile(false)
    }
  }

  async function handleFullSignOut(event?: ReactMouseEvent<HTMLAnchorElement>) {
    event?.preventDefault()
    setMessage('Clearing this browser and reopening Yan sign-in...')
    setError(null)
    await beginFullSignOutAndRestart('/account')
  }

  if (!isLoggedIn) return <Navigate to="/signin" replace />

  const accountName = displayName || [firstName, lastName].filter(Boolean).join(' ') || (email ? email.split('@')[0] : 'Member')
  const avatarLetter = accountName.slice(0, 1).toUpperCase()
  const showKnockoutEntry = rights['knockout.play'] === true
  const accessLabel = status === 'approved' ? 'Approved member' : status ? status.replace(/_/g, ' ') : 'Signed in'
  const questionnaireIsExternal = questionnaireUrl?.startsWith('https://')
  const confirmedAtLabel = formatConfirmedAt(profileConfirmedAt)

  return (
    <div className="account-page">
      {/* sidebar */}
      <aside className="account-sidebar">
        <div className="account-sidebar-badge">Yan-Vada &middot; member</div>
        <nav className="account-nav">
          {NAV_ITEMS.map((item, i) => (
            <button
              key={item}
              className={`account-nav-item${i === 0 ? ' active' : ''}`}
            >
              {item}
            </button>
          ))}
        </nav>
        <div className="account-sidebar-user">
          <div className="account-sidebar-label">Signed in as</div>
          <div className="account-sidebar-email">{email ?? '—'}</div>
          <button onClick={signOut} className="account-signout-btn">
            Sign out &rarr;
          </button>
        </div>
      </aside>

        {/* main */}
        <main className="account-main">
          <span className="legal-eyebrow">Account</span>
        <div className="account-hero">
          <div className="account-avatar" aria-hidden="true">
            {localImageUrl ? <img src={localImageUrl} alt="" /> : <span>{avatarLetter}</span>}
          </div>
          <div>
            <h1 className="account-name">{accountName}</h1>
            <p className="account-role">{profileType ?? 'member'} &middot; {email}</p>
          </div>
        </div>

        {/* status strip */}
        <div className="account-status-strip">
          <div className="account-status-cell">
            <div className="account-status-label">Access status</div>
            <div className="account-status-value account-status-success">{accessLabel}</div>
          </div>
          <div className="account-status-cell">
            <div className="account-status-label">Member area</div>
            <div className="account-status-value">Profile and session tools</div>
          </div>
          <div className="account-status-cell">
            <div className="account-status-label">Knockout</div>
            <div className={`account-status-value${showKnockoutEntry ? ' account-status-success' : ''}`}>{showKnockoutEntry ? 'Available' : 'Not assigned'}</div>
          </div>
        </div>

        {showKnockoutEntry && (
          <section className="account-knockout-card">
            <span className="legal-eyebrow">Member session</span>
            <h2>Your Knockout Session is ready.</h2>
            <p>Open the phone flow when you are ready to make your picks.</p>
            <Link to="/knockout" className="btn btn-primary">Open Knockout Session</Link>
          </section>
        )}

        {questionnaireUrl && (
          <section className="account-questionnaire-card">
            <span className="legal-eyebrow">Guest prep</span>
            <h2>Questionnaire</h2>
            <p>Answer the pre-recording questions before the session so the conversation can be tuned around you.</p>
            <a
              href={questionnaireUrl}
              className="btn btn-primary"
              target={questionnaireIsExternal ? '_blank' : undefined}
              rel={questionnaireIsExternal ? 'noreferrer' : undefined}
            >
              Open questionnaire
            </a>
          </section>
        )}

        {/* profile form */}
        <h2 className="account-section-heading">Profile</h2>
        {(message || error) && (
          <div className={`account-alert${error ? ' account-alert-error' : ''}`} role={error ? 'alert' : 'status'}>
            {error ?? message}
          </div>
        )}
        <section className="account-profile-image-card" aria-label="Profile image management">
          <div className="account-profile-image-preview" aria-hidden="true">
            {localImageUrl ? <img src={localImageUrl} alt="" /> : <span>{avatarLetter}</span>}
          </div>
          <div>
            <h3>Profile image</h3>
            <p>Use a clear square-ish image. JPG, PNG, or WebP up to 5 MB.</p>
          </div>
          <label className="account-profile-upload">
            <span>{uploading ? 'Uploading...' : 'Upload image'}</span>
            <input
              type="file"
              accept="image/jpeg,image/png,image/webp"
              disabled={uploading}
              onChange={e => void uploadProfileImage(e.currentTarget.files?.[0] ?? null)}
            />
          </label>
        </section>
        <form className="account-profile-form" onSubmit={saveProfileDetails}>
          <div className="account-profile-grid">
            <label>
              <span>First name</span>
              <input
                name="firstName"
                type="text"
                autoComplete="given-name"
                value={profileDraft.firstName}
                onChange={e => setProfileDraft(prev => ({ ...prev, firstName: e.currentTarget.value }))}
              />
            </label>
            <label>
              <span>Last name</span>
              <input
                name="lastName"
                type="text"
                autoComplete="family-name"
                value={profileDraft.lastName}
                onChange={e => setProfileDraft(prev => ({ ...prev, lastName: e.currentTarget.value }))}
              />
            </label>
            <label>
              <span>Work email</span>
              <input name="email" type="email" value={email ?? ''} readOnly />
              <small>Sign-in email. Change requires re-verification.</small>
            </label>
            <label>
              <span>Display name</span>
              <input
                name="displayName"
                type="text"
                autoComplete="name"
                value={profileDraft.displayName}
                onChange={e => setProfileDraft(prev => ({ ...prev, displayName: e.currentTarget.value }))}
              />
            </label>
          </div>
          <div className="account-form-actions">
            <button className="btn btn-primary" type="submit" disabled={savingProfile}>
              {savingProfile ? 'Saving...' : 'Save and confirm'}
            </button>
            <p className="account-form-note">
              {confirmedAtLabel ? `Last confirmed ${confirmedAtLabel}.` : 'Confirm these details before recording or sponsor calls.'}
            </p>
          </div>
        </form>

        {/* danger zone */}
        <h2 className="account-section-heading">Danger zone</h2>
        <div className="account-danger-zone">
          <div className="account-danger-info">
            <div className="account-danger-title">Full sign-out</div>
            <div className="account-danger-desc">
              Clears this browser and reopens Yan sign-in so you can choose another account.
            </div>
          </div>
          <a className="account-restart-btn" href={fullSignOutUrl('/account')} onClick={handleFullSignOut}>Restart sign-in</a>
        </div>
      </main>
    </div>
  )
}
